import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DialogContent, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

//import constants
import { BREAKPOINT, IS_MOBILE, WIDTH } from "../../../constants/breakpoints";
import BASE_URL from "../../../constants/baseUrls";

//import utils
import { scrollToPosition } from "../../../utils/utility";

//import general components
import BaseTitle from "../../common/base-title/base-title";
import Loading from "../../common/loading/loading";
import { BaseButton } from "../../common/base-button/base-button";
import BaseRating from "../../common/base-rating/base-rating";
import BaseCarousel from "../../common/base-carousel/base-carousel";
import BaseDialog from "../../common/base-dialog/base-dialog";

//import images and icons
import { ArrowForward, Location, Star1 } from "iconsax-react";

//import styles
import "./hotel-information.scss";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function HotelInformation() {
    //general variables
    let [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");
    const [isMapOpen, setIsMapOpen] = useState(false);
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));

    //sessionStorage variables
    const { isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation"));

    //scroll to top
    useEffect(() => {
        if (WIDTH < BREAKPOINT) scrollToPosition(0, 0);
        if (WIDTH > BREAKPOINT) scrollToPosition(350, 350);
    }, [loading]);

    // Add this function at the beginning of your component
    const getDisplayValue = value => {
        if (value === null || value === undefined || value === "" || value === "null, null") {
            return "-";
        }
        return value;
    };

    //request hotel details
    const [hotelDetails, setHotelDetails] = useState(null);
    const getHotelDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/v2/basic/accommodation/list/`, {
                params: {
                    pk: searchParams.get("hotelId"),
                    check_in: hotelInformation.checkIn,
                    check_out: hotelInformation.checkOut,
                    adults_count: 1,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setHotelDetails(response?.data?.data);
        } catch (error) {
            toast.error(t("errorMessage"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchParams.get("hotelId")) getHotelDetails();
    }, [searchParams]);

    //replace description
    const description = hotelDetails?.description?.replace("اقامت 24", "آتریپا");

    //list
    const detailList = [
        { id: 1, label: t("roomCounter"), value: hotelDetails?.room_count },
        { id: 2, label: t("checkIn"), value: "14:00" },
        { id: 3, label: t("checkOut"), value: "12:00" },
    ];

    //back to hotel list
    const handleGoBack = () => {
        const paramsToDelete = ["hotelId", "action", "tag"];
        paramsToDelete.forEach(param => searchParams.delete(param));
        setSearchParams(searchParams);
    };

    // Map modal handlers
    const handleOpenMap = () => setIsMapOpen(true);
    const handleCloseMap = () => setIsMapOpen(false);

    return (
        <Grid className="hotel-information" container item xs={12} rowGap={5} mb={5}>
            {loading && <Loading />}

            {!IS_MOBILE && (
                <BaseTitle
                    title={`${t("about")} ${t("hotel")} ${getDisplayValue(isHotelInternational ? hotelDetails?.english_name : hotelDetails?.name)}`}
                    dividerColor="1px solid var(--medium-gray-color)"
                    button={
                        <BaseButton
                            text={t("returnToHotel")}
                            color="outline-dark"
                            radius="radius-10"
                            size="small"
                            rightIcon={<ArrowForward size="16" color="var(--teal-color-18)" />}
                            handleClick={handleGoBack}
                        />
                    }
                />
            )}

            <Grid className="hotel-information__container" container item xs={12} rowGap={2} justifyContent="space-between" alignItems="flex-start">
                <Grid container item xs={12} md={5} rowGap={2.5} order={{ xs: 2, md: 1 }}>
                    <Grid container item xs={12} rowGap={{ xs: 1, md: 2 }}>
                        <Grid className="hotel-information__name" component="span" item xs={12} display={{ xs: "none", md: "flex" }}>
                            {t("hotel")} {isHotelInternational ? hotelDetails?.english_name : hotelDetails?.name}
                        </Grid>
                        <Grid className="hotel-information__address" container item xs={12} order={{ xs: 2, md: 1 }}>
                            {t("address")}: {isHotelInternational ? hotelDetails?.english_address : hotelDetails?.address}
                        </Grid>
                        <Grid container item xs={12} justifyContent="space-between" order={{ xs: 1, md: 2 }}>
                            <span className="hotel-information__rate">
                                {hotelDetails?.star} {t("star")}
                                <BaseRating
                                    value={hotelDetails?.star}
                                    icon={Star1}
                                    emptyIcon={Star1}
                                    color="var(--golden-color)"
                                    size="20"
                                    readOnly={true}
                                />
                            </span>
                        </Grid>
                    </Grid>

                    <Grid className="hotel-information__description" container item xs={12}>
                        <p>{description}</p>
                    </Grid>

                    <Grid container item xs={12} justifyContent="space-between">
                        {detailList.map(item => {
                            return (
                                <Grid
                                    className="hotel-information__details"
                                    container
                                    item
                                    xs={3.5}
                                    direction="column"
                                    rowGap={1}
                                    justifyContent="space-evenly"
                                    alignItems="center"
                                >
                                    <span className="hotel-information__label">{item.label}</span>
                                    <span className="hotel-information__value">{getDisplayValue(item.value)}</span>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                        <Grid className="hotel-information__section-title" container item xs={6}>
                            {t("contactInfo")}: <span className="phone-number">{getDisplayValue(hotelDetails?.phone_number)}</span>
                        </Grid>

                        <Grid container item xs={6}>
                            <BaseButton
                                text={t("showMap")}
                                color="primary"
                                radius="radius-10"
                                size="small"
                                rightIcon={<Location size="16" />}
                                handleClick={handleOpenMap}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container xs={12} item md={6} order={{ xs: 1, md: 2 }}>
                    <BaseCarousel
                        imageList={hotelDetails?.images}
                        dir="rtl"
                        height={IS_MOBILE ? "200px" : "450px"}
                        slidesPerView={1}
                        spaceBetween={10}
                        loop={true}
                        navigation={true}
                        pagination={true}
                        paginationType="bullets"
                        grid={1}
                        thumbsGallery={IS_MOBILE ? false : true}
                        baseUrl={isHotelInternational ? "" : "https://api.atripa.ir"}
                    />
                </Grid>
            </Grid>

            {/* Map Modal */}
            <BaseDialog title={getDisplayValue(hotelDetails?.english_name)} open={isMapOpen} handleClose={handleCloseMap}>
                <DialogContent dividers={true}>
                    {hotelDetails?.latitude && hotelDetails?.longitude && (
                        <MapContainer center={[hotelDetails.latitude, hotelDetails.longitude]} zoom={15} style={{ height: "400px", width: "100%" }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <Marker position={[hotelDetails.latitude, hotelDetails.longitude]}>
                                <Popup>
                                    {getDisplayValue(hotelDetails?.english_name)}
                                    <br />
                                    {getDisplayValue(hotelDetails?.english_address)}
                                </Popup>
                            </Marker>
                        </MapContainer>
                    )}
                </DialogContent>
            </BaseDialog>
        </Grid>
    );
}

export default HotelInformation;
